/**
 * useMerchantMenu - React Query hook for Admin Merchant Menu
 * Fetches categories + dishes from GET /api/admin/merchants/:merchantId/menu
 * Falls back to GET /api/v1/mobile/restaurants/:restaurantId/food-items when admin API unavailable.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import api from '../services/api';
import type {
  MerchantMenuResponse,
  MenuCategory,
  MenuDish,
  PatchDishPayload,
  PatchBulkDishesPayload,
  PatchCategoryPayload,
} from '../types/menu';

/** Legacy MenuItemRecord from mobile API - for adapter */
export interface MenuItemRecord {
  id: number;
  name: string;
  description?: string | null;
  price: number;
  cover_image?: string | null;
  image?: string | null;
  category_id?: number | null;
  is_available?: boolean;
  is_popular?: boolean;
  is_vegetarian?: boolean;
  tags?: string | null;
  rating?: number;
  review_count?: number;
  [key: string]: unknown;
}

const QUERY_KEY = (merchantId: string) =>
  ['admin', 'merchants', merchantId, 'menu'] as const;

/** Admin API category (snake_case) */
interface AdminCategoryRow {
  id: number;
  name: string;
  is_active?: boolean;
  sort_order?: number;
}

/** Merge all global categories with dish counts from current menu so "empty" categories (e.g. Nước) show in the list */
function mergeWithAllCategories(
  menu: MerchantMenuResponse,
  allCategories: AdminCategoryRow[]
): MerchantMenuResponse {
  const dishes = menu.dishes;
  const mergedCategories: MenuCategory[] = allCategories.map(cat => ({
    id: String(cat.id),
    name: cat.name,
    isActive: cat.is_active !== false,
    displayOrder: cat.sort_order ?? 0,
    dishCount: dishes.filter(d => d.categoryId === String(cat.id)).length,
  }));
  mergedCategories.sort(
    (a, b) =>
      a.displayOrder - b.displayOrder ||
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
  return { categories: mergedCategories, dishes };
}

/** Adapt legacy mobile API response to new structure */
function adaptLegacyToMenu(
  items: MenuItemRecord[],
  _restaurantId?: number
): MerchantMenuResponse {
  const dishes: MenuDish[] = [];

  for (const item of items) {
    const catId = item.category_id ?? null;
    const dishId = String(item.id);
    const dish: MenuDish = {
      id: dishId,
      categoryId: catId != null ? String(catId) : null,
      name: item.name,
      price: item.price,
      discountAmount: null,
      discountPercent: null,
      status: item.is_available !== false ? 'PUBLISHED' : 'HIDDEN',
      stockStatus: 'IN_STOCK',
      thumbnailUrl: item.cover_image || item.image || null,
      description: item.description || null,
      updatedAt: new Date().toISOString(),
    };
    dishes.push(dish);
  }

  return { categories: [], dishes };
}

function parseAdminCategoryRows(data: unknown): AdminCategoryRow[] {
  if (!Array.isArray(data)) return [];
  const rows: AdminCategoryRow[] = [];
  for (const row of data) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const id = r.id;
    if (id == null) continue;
    const name = String(r.name ?? '').trim();
    if (!name) continue;
    rows.push({
      id: Number(id),
      name,
      is_active: r.is_active !== false && r.isActive !== false,
      sort_order:
        typeof r.sort_order === 'number'
          ? r.sort_order
          : typeof r.sortOrder === 'number'
          ? r.sortOrder
          : 0,
    });
  }
  return rows;
}

/** Admin list (all categories). Falls back to public mobile list if admin route fails. */
async function fetchCategoriesForMenu(): Promise<AdminCategoryRow[]> {
  const attempts: { path: string; label: string }[] = [
    { path: '/admin/categories', label: 'admin' },
    { path: '/categories', label: 'admin-legacy' },
    { path: '/mobile/categories', label: 'mobile' },
  ];

  for (const { path } of attempts) {
    try {
      const res = await api.get<{ success?: boolean; data?: unknown }>(path);
      const rows = parseAdminCategoryRows(res.data?.data);
      if (rows.length > 0) return rows;
    } catch {
      // try next source
    }
  }
  return [];
}

async function fetchMerchantMenu(
  merchantId: string,
  restaurantId?: number
): Promise<MerchantMenuResponse> {
  let menuData: MerchantMenuResponse | null = null;
  let lastError: unknown;
  try {
    const res = await api.get<
      MerchantMenuResponse & { data?: MerchantMenuResponse }
    >(`/admin/merchants/${merchantId}/menu`);
    const payload = res.data?.data ?? res.data;
    if (payload && Array.isArray(payload.dishes)) {
      menuData = {
        categories: Array.isArray(payload.categories) ? payload.categories : [],
        dishes: payload.dishes,
      };
    }
  } catch (e) {
    lastError = e;
  }

  if (!menuData && restaurantId) {
    try {
      const res = await api.get<{ success?: boolean; data?: MenuItemRecord[] }>(
        `/mobile/restaurants/${restaurantId}/food-items`
      );
      if (res.data?.success && Array.isArray(res.data.data)) {
        menuData = adaptLegacyToMenu(res.data.data, restaurantId);
      }
    } catch (e) {
      lastError = e;
    }
  }

  if (!menuData) {
    throw lastError ?? new Error('Không thể tải menu. Vui lòng thử lại sau.');
  }

  const allCategories = await fetchCategoriesForMenu();
  if (allCategories.length > 0) {
    return mergeWithAllCategories(menuData, allCategories);
  }
  return menuData;
}

export function useMerchantMenu(
  merchantId: string | undefined,
  options?: { restaurantId?: number }
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEY(merchantId ?? ''),
    queryFn: () =>
      merchantId
        ? fetchMerchantMenu(merchantId, options?.restaurantId)
        : Promise.resolve({ categories: [], dishes: [] }),
    enabled: !!merchantId,
    placeholderData: keepPreviousData,
  });

  const patchDish = useMutation({
    mutationFn: async ({
      dishId,
      payload,
    }: {
      dishId: string;
      payload: PatchDishPayload;
    }) => {
      if (!merchantId) throw new Error('No merchant');
      const { adminNote, ...patch } = payload;
      const res = await api.patch(
        `/admin/merchants/${merchantId}/menu/dishes/${dishId}`,
        { ...patch, adminNote }
      );
      return res.data;
    },
    onMutate: async ({ dishId, payload }) => {
      await queryClient.cancelQueries({
        queryKey: QUERY_KEY(merchantId ?? ''),
      });
      const prev = queryClient.getQueryData<MerchantMenuResponse>(
        QUERY_KEY(merchantId ?? '')
      );
      if (prev) {
        const nextDishes = prev.dishes.map(d =>
          d.id === dishId
            ? {
                ...d,
                ...(payload.name != null && { name: payload.name }),
                ...(payload.description !== undefined && {
                  description: payload.description,
                }),
                ...(payload.price != null && { price: payload.price }),
                ...(payload.discountAmount !== undefined && {
                  discountAmount: payload.discountAmount,
                }),
                ...(payload.discountPercent !== undefined && {
                  discountPercent: payload.discountPercent,
                }),
                ...(payload.status && { status: payload.status }),
                ...(payload.stockStatus && {
                  stockStatus: payload.stockStatus,
                }),
                ...(payload.categoryId !== undefined && {
                  categoryId: payload.categoryId,
                }),
                ...(payload.coverImage !== undefined && {
                  thumbnailUrl: payload.coverImage,
                }),
                updatedAt: new Date().toISOString(),
              }
            : d
        );
        queryClient.setQueryData(QUERY_KEY(merchantId ?? ''), {
          ...prev,
          dishes: nextDishes,
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(QUERY_KEY(merchantId ?? ''), ctx.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(merchantId ?? '') });
    },
  });

  const patchBulk = useMutation({
    mutationFn: async (payload: PatchBulkDishesPayload) => {
      if (!merchantId) throw new Error('No merchant');
      const res = await api.patch(
        `/admin/merchants/${merchantId}/menu/dishes/bulk`,
        payload
      );
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(merchantId ?? '') });
    },
  });

  const patchCategory = useMutation({
    mutationFn: async ({
      categoryId,
      payload,
    }: {
      categoryId: string;
      payload: PatchCategoryPayload;
    }) => {
      if (!merchantId) throw new Error('No merchant');
      const { adminNote, ...patch } = payload;
      const res = await api.patch(
        `/admin/merchants/${merchantId}/menu/categories/${categoryId}`,
        { ...patch, adminNote }
      );
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(merchantId ?? '') });
    },
  });

  /** Admin tạo món ăn thay merchant */
  const createDish = useMutation({
    mutationFn: async (payload: CreateDishPayload) => {
      if (!merchantId) throw new Error('No merchant');
      const res = await api.post(
        `/admin/merchants/${merchantId}/menu/dishes`,
        payload
      );
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(merchantId ?? '') });
    },
  });

  /** Admin xóa món ăn */
  const deleteDish = useMutation({
    mutationFn: async (dishId: string) => {
      if (!merchantId) throw new Error('No merchant');
      const res = await api.delete(
        `/admin/merchants/${merchantId}/menu/dishes/${dishId}`
      );
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(merchantId ?? '') });
    },
  });

  /** Admin tạo danh mục (global category, dùng chung; hiển thị trong menu merchant khi có món thuộc danh mục) */
  const createCategory = useMutation({
    mutationFn: async (
      payload: CreateCategoryPayload
    ): Promise<ApiCategory | undefined> => {
      const res = await api.post<{ success?: boolean; data?: ApiCategory }>(
        '/admin/categories',
        payload
      );
      return res.data?.data;
    },
    onSuccess: (data: ApiCategory | undefined) => {
      if (!data || !merchantId) return;
      const newCat: MenuCategory = {
        id: String(data.id),
        name: data.name ?? '',
        isActive: data.is_active !== false,
        displayOrder: data.sort_order ?? 0,
        dishCount: 0,
      };
      queryClient.setQueryData<MerchantMenuResponse>(
        QUERY_KEY(merchantId),
        prev => {
          if (!prev) return prev;
          const exists = prev.categories.some(c => c.id === newCat.id);
          if (exists) return prev;
          return {
            ...prev,
            categories: [...prev.categories, newCat],
          };
        }
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(merchantId ?? '') });
    },
  });

  return {
    data: query.data,
    categories: query.data?.categories ?? [],
    dishes: query.data?.dishes ?? [],
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
    patchDish,
    patchBulk,
    patchCategory,
    createDish,
    createCategory,
    deleteDish,
    queryKey: QUERY_KEY(merchantId ?? ''),
  };
}

/** Payload for admin create dish */
export interface CreateDishPayload {
  name: string;
  price: number;
  description?: string | null;
  categoryId?: number | null;
  coverImage?: string | null;
  isAvailable?: boolean;
  adminNote?: string | null;
}

/** Payload for admin create category (POST /categories - global) */
export interface CreateCategoryPayload {
  name: string;
  description?: string;
}

/** Category as returned by API (snake_case) */
interface ApiCategory {
  id: number;
  name: string;
  description?: string;
  is_active?: boolean;
  sort_order?: number;
}
